import type { RandomParams, Video } from "@/types/video";

// All Bilibili data is requested by the visitor's browser.  This deliberately
// avoids routing media discovery through a Cloudflare Pages Function, whose
// server-side IPs may be rate-limited or blocked by Bilibili.
const BILI_API = "https://api.bilibili.com";
const REQUEST_TIMEOUT = 8_000;
const PARALLEL_PROBES = 8;
const PROBE_BATCHES = 3;

interface BiliViewData {
  bvid: string;
  aid: number;
  title: string;
  pic: string;
  owner?: { mid: number; name: string };
  pubdate: number;
  duration: number;
  stat?: { view?: number; like?: number };
  tname?: string;
  dimension?: { width: number; height: number; rotate?: number };
  state?: number;
}

interface BiliViewResponse {
  code: number;
  data?: BiliViewData;
}

interface AidRange {
  min: number;
  max: number;
  years: number[];
  weight: number;
}

const AID_RANGES: AidRange[] = [
  { min: 1, max: 100_000, years: [2009], weight: 0.4 },
  { min: 100_000, max: 800_000, years: [2010, 2011], weight: 1 },
  { min: 800_000, max: 3_000_000, years: [2012, 2013], weight: 1.5 },
  { min: 3_000_000, max: 10_000_000, years: [2014, 2015], weight: 2 },
  { min: 10_000_000, max: 40_000_000, years: [2016, 2017], weight: 2.5 },
  { min: 40_000_000, max: 150_000_000, years: [2017, 2018], weight: 2 },
  { min: 150_000_000, max: 600_000_000, years: [2019, 2020, 2021], weight: 1.5 },
  { min: 600_000_000, max: 3_000_000_000, years: [2022, 2023, 2024, 2025], weight: 0.8 },
];

// This is deliberately per-browser, rather than an edge/server cache.
const deadAids = new Map<number, number>();
const DEAD_AID_TTL = 5 * 60 * 1000;

function isDeadAid(aid: number): boolean {
  const timestamp = deadAids.get(aid);
  if (!timestamp) return false;
  if (Date.now() - timestamp < DEAD_AID_TTL) return true;
  deadAids.delete(aid);
  return false;
}

function markDeadAid(aid: number) {
  deadAids.set(aid, Date.now());
  if (deadAids.size > 2_000) {
    const oldest = deadAids.keys().next().value;
    if (oldest !== undefined) deadAids.delete(oldest);
  }
}

function pickRandomAid(year?: number | null, lost = false): number {
  let pool = AID_RANGES;
  if (year) {
    pool = AID_RANGES.filter((range) => range.years.includes(year));
    if (!pool.length) pool = AID_RANGES;
  }
  if (lost) {
    pool = AID_RANGES.filter((range) => range.max <= 20_000_000);
  }

  let random = Math.random() * pool.reduce((sum, range) => sum + range.weight, 0);
  let chosen = pool[pool.length - 1];
  for (const range of pool) {
    random -= range.weight;
    if (random <= 0) {
      chosen = range;
      break;
    }
  }
  return Math.floor(Math.random() * (chosen.max - chosen.min)) + chosen.min;
}

/**
 * Bilibili's public APIs do not grant arbitrary Pages domains permission to
 * read a `fetch` response. Its JSONP support, however, is designed for public
 * web pages: the script is loaded directly by the visitor's browser and no
 * Cloudflare worker participates in the request.
 */
async function directFetch<T>(path: string): Promise<T | null> {
  return new Promise((resolve) => {
    const callbackName = `__bilitime_jsonp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const callbackHost = window as unknown as Record<string, (data: T) => void>;
    const url = new URL(`${BILI_API}${path}`);
    url.searchParams.set("jsonp", "jsonp");
    url.searchParams.set("callback", callbackName);

    const script = document.createElement("script");
    const finish = (data: T | null) => {
      window.clearTimeout(timeout);
      delete callbackHost[callbackName];
      script.remove();
      resolve(data);
    };
    const timeout = window.setTimeout(() => finish(null), REQUEST_TIMEOUT);

    callbackHost[callbackName] = (data) => finish(data);
    script.async = true;
    script.src = url.toString();
    script.onerror = () => finish(null);
    document.head.append(script);
  });
}

/** Read only Bilibili's structured video-tag endpoint through the same-origin Function. */
async function fetchTags(bvid: string): Promise<string[]> {
  try {
    // Bump the schema key when the upstream response parser changes so a
    // previously cached successful-but-empty response cannot hide the fix.
    const response = await fetch(`/api/tags?bvid=${encodeURIComponent(bvid)}&schema=3`);
    if (!response.ok) return [];
    const payload = (await response.json()) as {
      success?: boolean;
      data?: { tags?: unknown };
    };
    return payload.success && Array.isArray(payload.data?.tags)
      ? payload.data.tags.filter((tag): tag is string => typeof tag === "string" && tag.length > 0)
      : [];
  } catch {
    return [];
  }
}

function isPortrait(video: BiliViewData): boolean {
  const dimension = video.dimension;
  if (!dimension) return false;
  const rotated = dimension.rotate === 90 || dimension.rotate === 270;
  const width = rotated ? dimension.height : dimension.width;
  const height = rotated ? dimension.width : dimension.height;
  return height > width;
}

function toVideo(video: BiliViewData, tags: string[]): Video {
  return {
    bvid: video.bvid,
    aid: video.aid,
    title: video.title.replace(/<[^>]*>/g, "").trim(),
    author: { mid: video.owner?.mid ?? null, name: video.owner?.name ?? "未知" },
    pubdate: video.pubdate,
    year: new Date(video.pubdate * 1000).getFullYear(),
    category: video.tname ?? "其他",
    duration: video.duration,
    view: video.stat?.view ?? 0,
    like: video.stat?.like ?? 0,
    cover: video.pic,
    channel: Math.floor(Math.random() * 999) + 1,
    tags,
  };
}

async function probeAid(aid: number, blockedTags: Set<string>): Promise<Video | null> {
  if (isDeadAid(aid)) return null;
  const response = await directFetch<BiliViewResponse>(`/x/web-interface/view?aid=${aid}`);
  const video = response?.code === 0 ? response.data : undefined;
  if (!video || !video.bvid || !video.duration || !video.title || video.state !== 0 || isPortrait(video)) {
    markDeadAid(aid);
    return null;
  }

  const tags = await fetchTags(video.bvid);
  if (tags.some((tag) => blockedTags.has(tag))) return null;
  return toVideo(video, tags);
}

export async function fetchRandom(params: RandomParams = {}, exclude: string[] = []): Promise<Video> {
  const blockedTags = new Set(params.blockedTags ?? []);
  const excluded = new Set(exclude);

  for (let batch = 0; batch < PROBE_BATCHES; batch++) {
    const aids = Array.from({ length: PARALLEL_PROBES }, () =>
      pickRandomAid(params.year, params.mode === "lost")
    );
    const results = await Promise.all(aids.map((aid) => probeAid(aid, blockedTags)));
    const found = results.find((video) => video && !excluded.has(video.bvid));
    if (found) return found;
  }
  throw new Error("NO_SIGNAL");
}

export async function fetchVideoById(bvid: string): Promise<Video | null> {
  const response = await directFetch<BiliViewResponse>(`/x/web-interface/view?bvid=${encodeURIComponent(bvid)}`);
  const video = response?.code === 0 ? response.data : undefined;
  if (!video || isPortrait(video)) return null;
  return toVideo(video, await fetchTags(video.bvid));
}

export interface HealthInfo {
  status: string;
  bilibili: boolean;
  source: string;
}

export async function fetchHealth(): Promise<HealthInfo> {
  const response = await directFetch<{ code: number }>("/x/web-interface/nav");
  return {
    status: response?.code === 0 ? "OK" : "OFFLINE",
    bilibili: response?.code === 0,
    source: "browser-direct",
  };
}

export function formatYear(year: number): string {
  return String(year);
}

export function formatNumber(n: number): string {
  return n >= 10000 ? `${(n / 10000).toFixed(1).replace(/\.0$/, "")}万` : n.toLocaleString("zh-CN");
}

export function formatDate(pubdate: number): string {
  const date = new Date(pubdate * 1000);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function bilibiliUrl(bvid: string): string {
  return `https://www.bilibili.com/video/${bvid}`;
}

export function embedUrl(bvid: string, autoplay = true, muted = false): string {
  const params = new URLSearchParams({
    bvid,
    isOutside: "true",
    autoplay: autoplay ? "1" : "0",
    muted: muted ? "1" : "0",
    playsinline: "1",
    danmaku: "0",
    nobtn: "1",
    fullscreen: "1",
  });
  return `https://player.bilibili.com/player.html?${params.toString()}`;
}
